import React from "react";
import { Button, Table, Tooltip } from "antd";
import {
  ExecutionMonthColumn,
  ExecutionRowKind,
  fmtINR,
  fmtINRCompact,
  fmtINRSigned,
  fmtMonth,
  num,
} from "./executionShared";

// The months-as-columns execution sheet, restructured per the Aug-27 business
// workbook: every month is a column GROUP of three sub-columns —
//   Target    = executed at PLANNED rates (the earned* fields)
//   Actual    = real consumed cost from the daily site captures (actual* fields)
//   Deviation = Target − Actual (cost efficiency; positive = under cost, green)
// Only the EXECUTION section uses the trio; PLAN / BILLING / WATERFALL rows span
// all three sub-columns (antd colSpan), mirroring the workbook's merged cells.
//
// TWO muting signals: isActualized (months past the last captured task actual)
// mutes the Target side and the waterfall; hasActualCosts (any consumed-cost
// capture for the month) mutes the Actual and Deviation sub-cells. The Deviation
// polarity here is the OPPOSITE of the old Executed−Planned volume deviation:
// spending less than the earned value is good and renders green.

interface ExecutionTableRowModel {
  key: ExecutionRowKind;
  section: "PLAN" | "EXECUTION (AS PER PLANNED)" | "BILLING" | "WATERFALL";
  label: string;
  hint?: string;
  emphasis?: boolean;
  signed?: boolean;          // ± value: color by sign
  signFlip?: boolean;        // for variance: negative = behind = red
  backlogRow?: boolean;      // backlog levels: positive = pending work (amber-ish ink)
  actualOnly?: boolean;      // Target side muted on non-actualized months
  clickable: boolean;
  /** "full" rows render one value spanning the month's three sub-columns. */
  span: "full" | "trio";
  targetField: keyof ExecutionMonthColumn;
  actualField?: keyof ExecutionMonthColumn;
  deviationField?: keyof ExecutionMonthColumn;
}

const ROWS: ExecutionTableRowModel[] = [
  {
    key: "clientPlan", section: "PLAN", label: "Planned Revenue (Client)",
    hint: "The billing program: scheduled quantity × Basic Rate (net of GST and labour cess), derived from the task monthly schedules — shown muted when derived. Use 'Edit Planned Revenue' only for months where the client's approved program differs; enter those amounts on the same net basis.",
    clickable: false, span: "full", targetField: "clientPlanAmount",
  },
  {
    key: "target", section: "PLAN", label: "Planned Expenditure",
    hint: "Planned execution COST for the month — the total of the 3Ms (Material + Manpower + Machinery incl. Equipment norms) phased by the schedules. The three rows below are this row's breakdown.",
    emphasis: true, clickable: true, span: "full", targetField: "targetAmount",
  },
  {
    key: "plannedMaterial", section: "PLAN", label: "Material (Planned)",
    hint: "Planned task-level quantity × the Material values from the weighted-average rates.",
    clickable: true, span: "full", targetField: "plannedMaterial",
  },
  {
    key: "plannedManpower", section: "PLAN", label: "Manpower (Planned)",
    hint: "Planned task-level quantity × the PRW values from the weighted-average rates.",
    clickable: true, span: "full", targetField: "plannedManpower",
  },
  {
    key: "plannedMachinery", section: "PLAN", label: "Machinery (Planned)",
    hint: "Planned task-level quantity × the Mucking & Machinery values from the weighted-average rates.",
    clickable: true, span: "full", targetField: "plannedMachinery",
  },
  {
    key: "executed", section: "EXECUTION (AS PER PLANNED)", label: "Executed Expenditure",
    hint: "Target: execution COST of the work actually done — the 3M basis × the captured actual quantities, at PLANNED rates. Actual: the real consumed cost from the daily site inputs. Deviation: Target − Actual.",
    emphasis: true, actualOnly: true, clickable: true, span: "trio",
    targetField: "executedAmount", actualField: "actualOutflow",
    deviationField: "outflowCostDeviation",
  },
  {
    key: "earnedMaterial", section: "EXECUTION (AS PER PLANNED)", label: "Material",
    hint: "Target: from Daily Execution at the 3M-Consolidation weighted-average (planned) rates. Actual: the 3M actual prices from material issues captured in the mobile app, at the site's moving-average purchase rate.",
    actualOnly: true, clickable: true, span: "trio",
    targetField: "earnedMaterial", actualField: "actualMaterial",
    deviationField: "materialCostDeviation",
  },
  {
    key: "earnedManpower", section: "EXECUTION (AS PER PLANNED)", label: "Manpower",
    hint: "Target: from Daily Execution at planned PRW rates. Actual: the approved time-log consumption (actual quantity × actual rate).",
    actualOnly: true, clickable: true, span: "trio",
    targetField: "earnedManpower", actualField: "actualManpower",
    deviationField: "manpowerCostDeviation",
  },
  {
    key: "earnedMachinery", section: "EXECUTION (AS PER PLANNED)", label: "Machinery",
    hint: "Target: from Daily Execution at planned machinery rates. Actual: daily equipment-log hours × the effective-dated Hourly Use Rate of each machine.",
    actualOnly: true, clickable: true, span: "trio",
    targetField: "earnedMachinery", actualField: "actualMachinery",
    deviationField: "machineryCostDeviation",
  },
  {
    key: "actualClientBilling", section: "BILLING", label: "Actual Client Billing",
    hint: "Quantities from Daily Execution × the Basic Rates — the billing value of the work actually done. Click a month for the activity-wise running-meter breakdown.",
    emphasis: true, actualOnly: true, clickable: true, span: "full",
    targetField: "actualClientBilling",
  },
  {
    key: "openingBacklog", section: "WATERFALL", label: "Opening Backlog",
    hint: "Execution value carried IN from earlier months (cumulative planned − executed before this month).",
    backlogRow: true, actualOnly: true, clickable: false, span: "full",
    targetField: "openingBacklog",
  },
  {
    key: "monthVariance", section: "WATERFALL", label: "Month Variance",
    hint: "Executed Expenditure − Planned Expenditure for the month. Negative (red) = shortfall that moves into the next month's opening backlog.",
    signed: true, signFlip: true, actualOnly: true, clickable: false, span: "full",
    targetField: "monthVariance",
  },
  {
    key: "closingBacklog", section: "WATERFALL", label: "Closing Backlog",
    hint: "Opening + Planned − Executed = the next month's opening backlog.",
    emphasis: true, backlogRow: true, actualOnly: true, clickable: false, span: "full",
    targetField: "closingBacklog",
  },
];

const sectionBadge: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.6,
  color: "#8094ae",
  display: "block",
  lineHeight: 1.1,
};

// Sub-column widths. scroll.x MUST be >= the sum of the rendered column widths
// or antd's fixed (frozen) columns misalign against the scroll body — compute
// it from these SAME literals, never re-derive.
const LABEL_W = 240;
const MONTH_TRIO_W = [104, 104, 112];
const TOTAL_TRIO_W = [116, 116, 124];
const sumOf = (w: number[]) => w.reduce((a, b) => a + b, 0);

type CellVariant = "target" | "actual" | "deviation";

const ExecutionTopSheetTable: React.FC<{
  months: ExecutionMonthColumn[];
  totals?: ExecutionMonthColumn;
  onCellClick: (month: string, kind: ExecutionRowKind) => void;
}> = ({ months, totals, onCellClick }) => {
  const dataSource = ROWS.map((row, index) => ({
    ...row,
    rowKey: row.key,
    firstOfSection: index === 0 || ROWS[index - 1].section !== row.section,
  }));
  type RowRecord = (typeof dataSource)[number];

  const mutedDash = (title: string) => (
    <Tooltip title={title}>
      <span style={{ color: "#c9d2e3" }}>—</span>
    </Tooltip>
  );

  const renderCell = (
    row: RowRecord,
    column: ExecutionMonthColumn | undefined,
    variant: CellVariant
  ) => {
    if (!column) return null;
    const isTotal = column.month === "TOTAL";

    if (variant !== "target") {
      if (row.span === "full") return null; // colSpan 0 — cell never shows
      // Actual/Deviation sub-cells live off the consumed-cost captures. The
      // Deviation additionally needs the Target side to exist.
      if (!column.hasActualCosts || (variant === "deviation" && !isTotal && !column.isActualized)) {
        return mutedDash("No consumed-cost captures (material issues / equipment logs / time logs) for this month yet");
      }
      const field = variant === "actual" ? row.actualField : row.deviationField;
      if (!field) return null;
      const value = num(column[field] as string);
      if (variant === "deviation") {
        // Cost efficiency: positive = spent LESS than the earned value = green.
        const color = value === 0 ? "#8c94a8" : value > 0 ? "#237804" : "#cf1322";
        return (
          <Tooltip title={`${fmtINRSigned(value, true)} — Target − Actual (positive = under cost)`}>
            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 500, color }}>
              {fmtINRSigned(value)}
            </span>
          </Tooltip>
        );
      }
      const label = (
        <span
          style={{
            fontVariantNumeric: "tabular-nums",
            fontWeight: row.emphasis ? 700 : 500,
            color: row.emphasis ? "#022c57" : undefined,
          }}
        >
          {fmtINRCompact(value)}
        </span>
      );
      if (!row.clickable || isTotal) return <Tooltip title={fmtINR(value)}>{label}</Tooltip>;
      return (
        <Tooltip title={`${fmtINR(value)} — click for ${fmtMonth(column.month)} detail`}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto" }}
            onClick={() => onCellClick(column.month, row.key)}
          >
            {label}
          </Button>
        </Tooltip>
      );
    }

    // ---- Target sub-cell (and the merged cell of every "full" row) ----
    // Months past the actualization boundary: executed/backlog cells are muted
    // em-dashes — a future month has no actuals, not "zero backlog".
    if (row.actualOnly && !isTotal && !column.isActualized) {
      return mutedDash("No actuals captured for this month yet");
    }
    const value = num(column[row.targetField] as string);
    let color: string | undefined = row.emphasis ? "#022c57" : undefined;
    if (row.signed) {
      // signFlip rows (variance): negative = behind plan = red.
      const bad = row.signFlip ? value < 0 : value > 0;
      color = value === 0 ? "#8c94a8" : bad ? "#cf1322" : "#237804";
    } else if (row.backlogRow) {
      color = value > 0 ? "#b46a12" : value < 0 ? "#237804" : "#8c94a8";
    }
    const text = row.signed || row.backlogRow ? fmtINRSigned(value) : fmtINRCompact(value);
    const exact = row.signed || row.backlogRow ? fmtINRSigned(value, true) : fmtINR(value);
    // A derived Planned Revenue month is an estimate from the schedules, not a figure the
    // client committed to — mute it the same way non-actualized cells are muted, so the
    // two kinds of number in this row are never confused.
    const isDerivedClientPlan =
      row.key === "clientPlan" && !isTotal && column.clientPlanIsDerived;
    if (isDerivedClientPlan) color = "#a9b6c9";
    const label = (
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: row.emphasis ? 700 : 500,
          color,
        }}
      >
        {text}
      </span>
    );
    if (isDerivedClientPlan) {
      return (
        <Tooltip
          title={`${exact} — estimated from schedules × Basic Rate. No planned revenue entered for ${fmtMonth(
            column.month
          )}.`}
        >
          <span style={{ cursor: "help" }}>{label}</span>
        </Tooltip>
      );
    }
    if (!row.clickable || isTotal) {
      return <Tooltip title={exact}>{label}</Tooltip>;
    }
    return (
      <Tooltip title={`${exact} — click for ${fmtMonth(column.month)} detail`}>
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: "auto" }}
          onClick={() => onCellClick(column.month, row.key)}
        >
          {label}
        </Button>
      </Tooltip>
    );
  };

  const subHeader = (text: string, tip: string) => (
    <Tooltip title={tip}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#526484" }}>{text}</span>
    </Tooltip>
  );

  // "full" rows render their value in the Target child spanning the trio; the
  // sibling children collapse to colSpan 0. The colSpan must come from the
  // COLUMN's onCell so antd drops the sibling <td>s entirely.
  const trioChildren = (
    column: ExecutionMonthColumn,
    widths: number[],
    extraCellStyle?: React.CSSProperties
  ) => {
    const onCell = (variant: CellVariant) => (row: RowRecord) => ({
      colSpan: row.span === "full" ? (variant === "target" ? 3 : 0) : 1,
      ...(extraCellStyle ? { style: extraCellStyle } : {}),
    });
    return [
      {
        title: subHeader("Target", "Executed at planned rates — Daily Execution quantities × the 3M-Consolidation weighted-average rates"),
        key: `${column.month}-target`,
        align: "right" as const,
        width: widths[0],
        onCell: onCell("target"),
        render: (_: any, row: RowRecord) => renderCell(row, column, "target"),
      },
      {
        title: subHeader("Actual", "Real consumed cost from the daily site captures (material issues, equipment logs, time logs)"),
        key: `${column.month}-actual`,
        align: "right" as const,
        width: widths[1],
        onCell: onCell("actual"),
        render: (_: any, row: RowRecord) => renderCell(row, column, "actual"),
      },
      {
        title: subHeader("Deviation", "Target − Actual (cost efficiency): positive = under cost"),
        key: `${column.month}-deviation`,
        align: "right" as const,
        width: widths[2],
        onCell: onCell("deviation"),
        render: (_: any, row: RowRecord) => renderCell(row, column, "deviation"),
      },
    ];
  };

  const columns: any[] = [
    {
      title: "Particulars",
      key: "label",
      fixed: "left" as const,
      width: LABEL_W,
      render: (_: any, row: RowRecord) => (
        <div>
          {row.firstOfSection && <span style={sectionBadge}>{row.section}</span>}
          <Tooltip title={row.hint}>
            <span
              style={{
                fontWeight: row.emphasis ? 700 : 500,
                color: "#022c57",
                ...(row.hint
                  ? { textDecoration: "underline dotted #b6c2d9", textUnderlineOffset: 3 }
                  : {}),
              }}
            >
              {row.label}
            </span>
          </Tooltip>
        </div>
      ),
    },
    ...months.map((column) => ({
      title: (
        <span style={{ opacity: column.isActualized ? 1 : 0.55 }}>
          {fmtMonth(column.month)}
        </span>
      ),
      key: column.month,
      align: "center" as const,
      children: trioChildren(column, MONTH_TRIO_W),
    })),
    ...(totals
      ? [
          {
            title: "Total",
            key: "total",
            fixed: "right" as const,
            align: "center" as const,
            children: trioChildren(totals, TOTAL_TRIO_W, { background: "#f7f9fc" }),
          },
        ]
      : []),
  ];

  return (
    <Table
      size="small"
      bordered
      sticky
      className="customTable"
      rowKey="rowKey"
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      scroll={{
        x: LABEL_W + sumOf(MONTH_TRIO_W) * months.length + sumOf(TOTAL_TRIO_W),
      }}
    />
  );
};

export default ExecutionTopSheetTable;
